import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setUnverified(false);
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setUnverified(true);
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!email.trim()) return;
    setResendStatus('sending');
    try {
      await api.resendVerification(email.trim());
    } catch {
      /* always returns 204; ignore */
    }
    setResendStatus('sent');
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Sign in</h1>
        <div className="sub">Revenue Estimator</div>
        {error && <div className="error-banner">{error}</div>}
        {unverified && (
          <div className="notice-banner">
            This email isn't verified yet. Check your inbox for the
            confirmation link.{' '}
            {resendStatus === 'idle' && (
              <button
                type="button"
                className="link-btn"
                onClick={resend}
              >
                Resend the verification email
              </button>
            )}
            {resendStatus === 'sending' && <em>Sending…</em>}
            {resendStatus === 'sent' && (
              <em>Sent — check your inbox.</em>
            )}
          </div>
        )}
        <form className="auth-form" onSubmit={onSubmit}>
          <div className="field">
            <label>Email</label>
            <input
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label>Password</label>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button className="btn" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="foot-link">
          <Link to="/forgot-password">Forgot your password?</Link>
        </div>
        <div className="foot-link">
          No account? <Link to="/register">Create one</Link>
        </div>
      </div>
    </div>
  );
}
